import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Dropdown,
  Input,
} from "antd";
import React, { useCallback, useMemo, useState } from "react";
import {
  PrinterOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  GET_PROFIT_EXPORT,
  GET_PROFIT_EXPORT_DEV,
  GET_PROFIT_REPORT,
  GET_PROFIT_REPORT_DEV,
  HEADERS_EXPORT_DETAIL,
} from "../../API/apiUrls";
import CurrencyFormat from "../../../hook/CurrencyFormat";
import "../../../assets/font/THSarabunNew-normal";
import "../../../assets/font/THSarabunNew-bold";
import MotionHoc from "../../../utils/MotionHoc";
import TokenCheck from "../../../hook/TokenCheck";
import ThaiDatePicker from "../../../hook/ThaiDatePicker";
import XLSX from "xlsx-js-style";
import SearchContno from "./modal/SearchContno";

const { Text } = Typography;
const TwoLineHeader = ({ top, bottom }) => (
  <div style={{ textAlign: "center", lineHeight: 1.4 }}>
    <div>{top}</div>
    <div style={{ color: "#888" }}>{bottom}</div>
  </div>
);

// ── Summary helper ─────────────────────────────────────
const calcSummary = (data) =>
  data.reduce(
    (acc, r) => ({
      cost_debtor: acc.cost_debtor + (parseFloat(r.cost_debtor) || 0),
      total_profit: acc.total_profit + (parseFloat(r.total_profit) || 0),
      cash_amount: acc.cash_amount + (parseFloat(r.cash_amount) || 0),
      discount: acc.discount + (parseFloat(r.discount) || 0),
      penalty: acc.penalty + (parseFloat(r.penalty) || 0),
      net_payment: acc.net_payment + (parseFloat(r.net_payment) || 0),
      historical_total:
        acc.historical_total + (parseFloat(r.historical_total) || 0),
      historical_profit:
        acc.historical_profit + (parseFloat(r.historical_profit) || 0),
      cut_principal: acc.cut_principal + (parseFloat(r.cut_principal) || 0),
      cut_profit: acc.cut_profit + (parseFloat(r.cut_profit) || 0),
      remaining_cost: acc.remaining_cost + (parseFloat(r.remaining_cost) || 0),
      remaining_profit:
        acc.remaining_profit + (parseFloat(r.remaining_profit) || 0),
    }),
    {
      cost_debtor: 0,
      total_profit: 0, // ✅ เพิ่ม
      cash_amount: 0,
      discount: 0,
      penalty: 0,
      net_payment: 0,
      historical_total: 0,
      historical_profit: 0, // ✅ เพิ่ม
      cut_principal: 0,
      cut_profit: 0,
      remaining_cost: 0,
      remaining_profit: 0,
    },
  );

const Main = () => {
  const [currencyFormat, currencyFormatComma, currencyFormatPoint] =
    CurrencyFormat();
  const [signOut] = TokenCheck();
  const [loading, setLoading] = useState(false);
  const [arrayPay, setArrayPay] = useState([]);
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs());
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
  });
  const [currentPayload, setCurrentPayload] = useState({});
  const [isModal, setIsModal] = useState(false);
  const [searchContno, setSearchContno] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState("");

  // ── Query ─────────────────────────────────────────────
  const onQuery = () => {
    if (!startDate || !endDate) {
      message.warning("กรุณาเลือกวันที่ก่อนค้นหา");
      return;
    }
    const payload = {
      start_date: dayjs(startDate).format("YYYY-MM-DD"),
      end_date: dayjs(endDate).format("YYYY-MM-DD"),
    };
    handleSearch(payload); // ✅ ผ่าน handleSearch เพื่อ reset page 1
  };

  const queryData = async (payload, page = 1, limit = 50) => {
    setLoading(true);
    try {
      const res = await axios.get(GET_PROFIT_REPORT_DEV, {
        headers: HEADERS_EXPORT_DETAIL,
        params: { ...payload, page, limit },
      });
      console.log("res", res);
      if (res.status !== 200) {
        setArrayPay([]);
        return;
      }

      const sortedData = [...(res.data?.data ?? [])]
        .sort(
          (a, b) =>
            dayjs(a.receipt_date).valueOf() - dayjs(b.receipt_date).valueOf(),
        )
        .map((item, index) => ({
          ...item,
          key: (page - 1) * limit + index + 1,
        })); // key ต่อเนื่องตาม page

      const totalPages = res.data?.total_pages ?? 0;

      setArrayPay(sortedData);
      setPagination((prev) => ({
        ...prev,
        page,
        limit,
        total: totalPages * limit, // ✅ API ส่ง total_pages มาแทน total records
      }));
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) signOut();
      setArrayPay([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (values) => {
    setCurrentPayload(values);
    queryData(values, 1, pagination.limit); // reset กลับ page 1 เสมอ
  };

  // ── Filter (เลขสัญญา / เลขที่บิล) ───────────────────────
  const handleFilterData = useCallback(() => {
    if (!arrayPay.length) return [];

    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return arrayPay;

    return arrayPay.filter((item) => {
      const contractNo = (item.contract_no || "").toLowerCase();
      const receiptNo = (item.receipt_no || "").toLowerCase();
      return contractNo.includes(keyword) || receiptNo.includes(keyword);
    });
  }, [arrayPay, searchKeyword]);

  const exportExcelApi = async () => {
    if (!startDate || !endDate) {
      message.warning("กรุณาเลือกวันที่ก่อน Export");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(GET_PROFIT_EXPORT_DEV, {
        headers: HEADERS_EXPORT_DETAIL,
        responseType: "blob", // ✅ บอก axios ว่า response เป็น binary
        params: {
          start_date: dayjs(startDate).format("YYYY-MM-DD"),
          end_date: dayjs(endDate).format("YYYY-MM-DD"),
        },
      });

      if (res.status === 200) {
        // ✅ สร้าง URL จาก blob แล้ว trigger download
        const blob = new Blob([res.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        const filename = `รายงานรับรู้กำไร_${dayjs(startDate).format("YYYY-MM-DD")}_to_${dayjs(endDate).format("YYYY-MM-DD")}.xlsx`;

        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        // ✅ cleanup
        link.remove();
        window.URL.revokeObjectURL(url);

        message.success("Export Excel สำเร็จ");
      }
    } catch (err) {
      message.error(
        err?.response?.status === 404 ? "ไม่พบข้อมูล" : "เกิดข้อผิดพลาด",
      );
      if (err?.response?.status === 401) signOut();
    } finally {
      setLoading(false);
    }
  };

  // ── Columns ───────────────────────────────────────────
  const columns = [
    {
      title: "ลำดับ",
      dataIndex: "key",
      align: "center",
      width: 60,
      render: (val) => val, // ✅ ใช้ key ที่คำนวณจาก (page-1)*limit + index แล้ว
    },
    {
      title: "เลขสัญญา",
      dataIndex: "contract_no",
      align: "center",
      width: 130,
      render: (_, r) => <Text copyable>{r.contract_no}</Text>,
    },
    {
      title: "วันที่รับชำระ",
      dataIndex: "receipt_date",
      align: "center",
      width: 120,
      render: (_, r) => <>{dayjs(r.receipt_date).format("DD MMM YY")}</>,
    },
    {
      title: "เลขที่บิล",
      dataIndex: "receipt_no",
      align: "center",
      width: 120,
      render: (_, r) => <Text copyable>{r.receipt_no}</Text>,
    },
    {
      title: <TwoLineHeader top="ต้นทุนเดิม" bottom="กำไรทั้งหมด" />,
      align: "right",
      width: 140,
      render: (_, r) => (
        <>
          <div>{currencyFormatPoint(r.cost_debtor)}</div>
          <div style={{ color: "#888" }}>
            {currencyFormatPoint(r.total_profit)}
          </div>
        </>
      ),
    },
    {
      title: "รับเงินสด",
      align: "right",
      width: 110,
      render: (_, r) => currencyFormatPoint(r.cash_amount),
    },
    {
      title: "ส่วนลด",
      align: "right",
      width: 110,
      render: (_, r) => currencyFormatPoint(r.discount),
    },
    {
      title: "เบี้ยปรับ",
      align: "right",
      width: 110,
      render: (_, r) => currencyFormatPoint(r.penalty ?? 0),
    },
    {
      title: "ยอดสุทธิ(NET)",
      align: "right",
      width: 130,
      render: (_, r) => currencyFormatPoint(r.net_payment ?? 0),
    },
    {
      title: <TwoLineHeader top="ยอดสะสมอดีต" bottom="ดอกเบี้ยสะสม" />,
      align: "right",
      width: 140,
      render: (_, r) => (
        <>
          <div>{currencyFormatPoint(r.historical_total)}</div>
          <div style={{ color: "#888" }}>
            {currencyFormatPoint(r.historical_profit)}
          </div>
        </>
      ),
    },
    {
      title: <TwoLineHeader top="ตัดทุนเดิม" bottom="ตัดดอกเบี้ย" />,
      align: "right",
      width: 140,
      render: (_, r) => (
        <>
          <div>{currencyFormatPoint(r.cut_principal ?? 0)}</div>
          <div>{currencyFormatPoint(r.cut_profit ?? 0)}</div>
        </>
      ),
    },
    {
      title: <TwoLineHeader top="ตัดทุนคงเหลือ" bottom="กำไรคงเหลือ" />,
      align: "right",
      width: 140,
      render: (_, r) => (
        <>
          <div>{currencyFormatPoint(r.remaining_cost ?? 0)}</div>
          <div>{currencyFormatPoint(r.remaining_profit ?? 0)}</div>
        </>
      ),
    },
    {
      title: "ประเภทรับ",
      align: "center",
      width: 120,
      render: (_, r) => r.other_debtor ?? "-",
    },
  ];

  // ── Export Excel ──────────────────────────────────────
  const handleExportExcel = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const fmt = (d) => (d ? dayjs(d).format("DD/MM/YYYY") : "");
    const fromDate = fmt(startDate);
    const toDate = fmt(endDate);

    const headers = [
      "ลำดับ",
      "เลขสัญญา",
      "วันที่รับชำระ",
      "เลขที่บิล",
      "ต้นทุนเดิม",
      "กำไรทั้งหมด(%)",
      "รับเงินสด",
      "ส่วนลด",
      "เบี้ยปรับ",
      "ยอดสุทธิ(NET)",
      "ยอดสะสมอดีต",
      "ดอกเบี้ยสะสม(%)",
      "ตัดทุนเดิม",
      "ตัดดอกเบี้ย",
      "ตัดทุนคงเหลือ",
      "กำไรคงเหลือ",
      "ประเภทรับ",
    ];

    const moneyKeys = [
      "ต้นทุนเดิม",
      "รับเงินสด",
      "ส่วนลด",
      "เบี้ยปรับ",
      "ยอดสุทธิ(NET)",
      "ยอดสะสมอดีต",
      "ตัดทุนเดิม",
      "ตัดดอกเบี้ย",
      "ตัดทุนคงเหลือ",
      "กำไรคงเหลือ",
    ];

    const rows = filteredData.map((r, i) => ({
      ลำดับ: i + 1,
      เลขสัญญา: r.contract_no || "",
      วันที่รับชำระ: dayjs(r.receipt_date).format("DD MMM YY") || "",
      เลขที่บิล: r.receipt_no || "",
      ต้นทุนเดิม: parseFloat(r.cost_debtor) || 0,
      "กำไรทั้งหมด(%)": parseFloat(r.total_profit) || 0,
      รับเงินสด: parseFloat(r.cash_amount) || 0,
      ส่วนลด: parseFloat(r.discount) || 0,
      เบี้ยปรับ: parseFloat(r.penalty) || 0,
      "ยอดสุทธิ(NET)": parseFloat(r.net_payment) || 0,
      ยอดสะสมอดีต: parseFloat(r.historical_total) || 0,
      "ดอกเบี้ยสะสม(%)": parseFloat(r.historical_profit) || 0,
      ตัดทุนเดิม: parseFloat(r.cut_principal) || 0,
      ตัดดอกเบี้ย: parseFloat(r.cut_profit) || 0,
      ตัดทุนคงเหลือ: parseFloat(r.remaining_cost) || 0,
      กำไรคงเหลือ: parseFloat(r.remaining_profit) || 0,
      ประเภทรับ: r.other_debtor || "-",
    }));

    const t = calcSummary(filteredData);
    const summaryRow = {
      ลำดับ: "",
      เลขสัญญา: `รวมทั้งหมด ${filteredData.length} รายการ`,
      เลขที่บิล: "",
      ต้นทุนเดิม: t.cost_debtor,
      "กำไรทั้งหมด(%)": "",
      รับเงินสด: t.cash_amount,
      ส่วนลด: t.discount,
      เบี้ยปรับ: t.penalty,
      "ยอดสุทธิ(NET)": t.net_payment,
      ยอดสะสมอดีต: t.historical_total,
      "ดอกเบี้ยสะสม(%)": "",
      ตัดทุนเดิม: t.cut_principal,
      ตัดดอกเบี้ย: t.cut_profit,
      ตัดทุนคงเหลือ: t.remaining_cost,
      กำไรคงเหลือ: t.remaining_profit,
      ประเภทรับ: "",
    };

    const allRows = [...rows, summaryRow];
    const ws = XLSX.utils.json_to_sheet(allRows, { header: headers });

    // ── Header style ──
    const headerFill = { patternType: "solid", fgColor: { rgb: "34495E" } };
    const evenFill = { patternType: "solid", fgColor: { rgb: "EBF5FF" } };
    const oddFill = { patternType: "solid", fgColor: { rgb: "FFFFFF" } };
    const summaryFill = { patternType: "solid", fgColor: { rgb: "D5E8D4" } };
    const border = {
      top: { style: "thin", color: { rgb: "CCCCCC" } },
      bottom: { style: "thin", color: { rgb: "CCCCCC" } },
      left: { style: "thin", color: { rgb: "CCCCCC" } },
      right: { style: "thin", color: { rgb: "CCCCCC" } },
    };

    headers.forEach((key, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: ci });
      if (!ws[cellRef]) ws[cellRef] = { v: key, t: "s" };
      ws[cellRef].s = {
        fill: headerFill,
        font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" },
        border,
      };
    });

    // ── Data rows style ──
    rows.forEach((row, ri) => {
      const fill = ri % 2 === 0 ? evenFill : oddFill;
      headers.forEach((key, ci) => {
        const cellRef = XLSX.utils.encode_cell({ r: ri + 1, c: ci });
        if (!ws[cellRef]) ws[cellRef] = { v: "", t: "s" };
        const isMoney = moneyKeys.includes(key);
        const isPercent = key.includes("%");
        ws[cellRef].s = {
          fill,
          font: { sz: 11 },
          alignment: {
            horizontal: typeof row[key] === "number" ? "right" : "left",
            vertical: "center",
          },
          border,
          numFmt: isMoney ? "#,##0.00" : isPercent ? '0.00"%"' : undefined,
        };
      });
    });

    // ── Summary row style ──
    const summaryIdx = rows.length + 1;
    headers.forEach((key, ci) => {
      const cellRef = XLSX.utils.encode_cell({ r: summaryIdx, c: ci });
      if (!ws[cellRef]) return;
      const isMoney = moneyKeys.includes(key);
      ws[cellRef].s = {
        fill: summaryFill,
        font: { bold: true, sz: 12 },
        alignment: {
          horizontal: isMoney ? "right" : "left",
          vertical: "center",
        },
        border,
        numFmt: isMoney ? "#,##0.00" : undefined,
      };
    });

    ws["!cols"] = headers.map((key) => ({
      wch: Math.max(key.length * 2.5, 16),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "รายงาน");
    XLSX.writeFile(
      wb,
      `รายงานรับรู้กำไรตามวันที่ชำระ_${fromDate}_${toDate}.xlsx`,
    );
    message.success("Export Excel สำเร็จ");
  };

  // ── Export PDF ────────────────────────────────────────

  const handlePrintPDF = () => {
    if (!filteredData.length) {
      message.warning("ไม่มีข้อมูล");
      return;
    }

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = dayjs().format("DD/MM/YYYY HH:mm:ss");
    const fromDate = startDate ? dayjs(startDate).format("DD/MM/YYYY") : "";
    const toDate = endDate ? dayjs(endDate).format("DD/MM/YYYY") : "";

    // ── Header แต่ละหน้า ──────────────────────────────────
    const drawHeader = (pageNum) => {
      doc.setFont("THSarabunNew", "bold");
      doc.setFontSize(16);
      doc.text("รายงานรับรู้กำไรตามวันที่ชำระ", pageWidth / 2, 8, {
        align: "center",
      });
      doc.setFontSize(14);
      doc.text(
        `ตั้งแต่วันที่ ${fromDate} ถึงวันที่ ${toDate}`,
        pageWidth / 2,
        14,
        { align: "center" },
      );
      doc.setFont("THSarabunNew", "normal");
      doc.setFontSize(12);
      doc.text("บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)", 8, 20);
      doc.text(
        "89 หมู่ 14 ถ.มิตรภาพ ต.ในเมือง อ.เมืองขอนแก่น จ.ขอนแก่น 40000",
        8,
        25,
      );
      doc.text(`วันที่พิมพ์: ${now}`, 8, 30);
      doc.text(`หน้าที่ ${pageNum}`, pageWidth - 8, 30, { align: "right" });
    };

    // ── Table Header ──────────────────────────────────────
    const tableHead = [
      [
        "ลำดับ",
        "เลขสัญญา",
        "วันที่รับชำระ",
        "เลขที่บิล",
        "ต้นทุนเดิม\nกำไรทั้งหมด",
        "รับเงินสด",
        "ส่วนลด",
        "เบี้ยปรับ",
        "ยอดสุทธิ (NET)",
        "ยอดสะสมอดีต\nดอกเบี้ยสะสม",
        "ตัดทุนเดิม\nตัดดอกเบี้ย",
        "ทุนคงเหลือ\nกำไรคงเหลือ",
        "ประเภทรับ",
      ],
    ];

    // ── Table Body ────────────────────────────────────────
    const tableBody = filteredData.map((r) => {
      return [
        r.key, // ✅ ใช้ key ต่อเนื่องข้าม page
        r.contract_no || "-",
        dayjs(r.receipt_date).format("DD MMM YY") || "-",
        r.receipt_no || "-",
        `${currencyFormatPoint(r.cost_debtor)}\n${currencyFormatPoint(r.total_profit)}`, // ✅ format ตัวเลข
        currencyFormatPoint(r.cash_amount),
        currencyFormatPoint(r.discount),
        currencyFormatPoint(r.penalty ?? 0),
        currencyFormatPoint(r.net_payment ?? 0),
        `${currencyFormatPoint(r.historical_total)}\n${currencyFormatPoint(r.historical_profit)}`, // ✅ format ตัวเลข
        `${currencyFormatPoint(r.cut_principal ?? 0)}\n${currencyFormatPoint(r.cut_profit)}`,
        `${currencyFormatPoint(r.remaining_cost ?? 0)}\n${currencyFormatPoint(r.remaining_profit ?? 0)}`,
        r.other_debtor || "-",
      ];
    });

    // ── Summary Row ───────────────────────────────────────
    const t = calcSummary(filteredData);
    const cs = {
      fontStyle: "bold",
      font: "THSarabunNew",
      fillColor: [44, 62, 80],
      textColor: 255,
    };
    const cr = {
      fontStyle: "bold",
      font: "THSarabunNew",
      fillColor: [236, 240, 241],
      halign: "right",
    };

    const summaryRow = [
      {
        content: `รวมทั้งหมด ${filteredData.length.toLocaleString()} รายการ`,
        colSpan: 4,
        styles: { ...cs, halign: "center" },
      },
      {
        content: `${currencyFormatPoint(t.cost_debtor)}\n${currencyFormatPoint(t.total_profit)}`,
        styles: cr,
      },
      { content: currencyFormatPoint(t.cash_amount), styles: cr },
      { content: currencyFormatPoint(t.discount), styles: cr },
      { content: currencyFormatPoint(t.penalty), styles: cr },
      {
        content: currencyFormatPoint(t.net_payment),
        styles: { ...cr, fillColor: [212, 245, 220] },
      }, // ✅ เน้นสีเขียว
      {
        content: `${currencyFormatPoint(t.historical_total)}\n${currencyFormatPoint(t.historical_profit)}`, // ✅ เพิ่ม historical_profit
        styles: cr,
      },
      {
        content: `${currencyFormatPoint(t.cut_principal)}\n${currencyFormatPoint(t.cut_profit)}`,
        styles: cr,
      },
      {
        content: `${currencyFormatPoint(t.remaining_cost)}\n${currencyFormatPoint(t.remaining_profit)}`,
        styles: cr,
      },
      { content: "-", styles: { ...cs, halign: "center" } },
    ];

    // ── autoTable ─────────────────────────────────────────
    doc.autoTable({
      startY: 34,
      head: tableHead,
      body: [...tableBody, summaryRow],
      styles: {
        font: "THSarabunNew",
        fontSize: 11,
        cellPadding: 2,
        halign: "center",
        valign: "middle", // ✅ จัดกลางแนวตั้ง
        fillColor: [255, 255, 255],
      },
      headStyles: {
        font: "THSarabunNew",
        fillColor: [44, 62, 80], // ✅ สีเดียวกับ summary header
        textColor: 255,
        halign: "center",
        fontStyle: "bold",
        fontSize: 12,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 14, halign: "center" },
        1: { cellWidth: 28 },
        2: { cellWidth: 28 },
        3: { halign: "right" },
        4: { halign: "right" },
        5: { halign: "right" },
        6: { halign: "right" },
        7: { halign: "right" },
        8: { halign: "right" },
        9: { halign: "right" },
        10: { halign: "right" },
        11: { halign: "center" },
      },
      alternateRowStyles: { fillColor: [235, 245, 255] },
      didDrawPage: () =>
        drawHeader(doc.internal.getCurrentPageInfo().pageNumber),
      margin: { top: 34, left: 3, right: 3 },
    });

    doc.save(`รายงานรับรู้กำไรตามวันที่ชำระ_${fromDate}_${toDate}.pdf`);
    message.success("Export PDF สำเร็จ");
  };

  // ── Dropdown ──────────────────────────────────────────
  const exportMenuItems = {
    items: [
      {
        key: "pdf",
        label: "Export PDF",
        icon: <FilePdfOutlined style={{ color: "#c0392b" }} />,
      },
      {
        key: "excel",
        label: "Export Excel",
        icon: <FileExcelOutlined style={{ color: "#27ae60" }} />,
      },
    ],
    onClick: ({ key }) => {
      if (key === "pdf") handlePrintPDF();
      // if (key === "excel") handleExportExcel();
      if (key === "excel") exportExcelApi();
    },
  };

  // ── Table Summary ─────────────────────────────────────
  const tableSummary = () => {
    const t = calcSummary(filteredData);

    const cellBase = {
      fontWeight: "bold",
      background: "#f0f4f8",
      borderTop: "2px solid #2c3e50",
    };
    const label = (text, color = "#888") => (
      <div style={{ fontSize: 14, color, fontWeight: 400, marginBottom: 1 }}>
        {text}
      </div>
    );
    const val = (v, color = "#2c3e50") => (
      <div style={{ color, fontSize: 16 }}>{currencyFormatPoint(v)}</div>
    );

    return (
      <Table.Summary.Row>
        <Table.Summary.Cell
          index={0}
          colSpan={4}
          style={{ ...cellBase, background: "#2c3e50" }}
        >
          <div style={{ color: "#fff", textAlign: "center" }}>
            <div style={{ fontSize: 14, opacity: 0.75 }}>รวมทั้งหมด</div>
            <div style={{ fontSize: 16 }}>
              {searchKeyword.trim()
                ? filteredData.length.toLocaleString()
                : pagination.total.toLocaleString()}{" "}
              รายการ
            </div>
          </div>
        </Table.Summary.Cell>

        {/* index=3 → ต้นทุนเดิม */}
        <Table.Summary.Cell index={3} align="right" style={cellBase}>
          {label("ต้นทุนเดิม")}
          {val(t.cost_debtor, "#c0392b")}
          {label("กำไรทั้งหมด")}
          {val(t.total_profit, "#888")}
        </Table.Summary.Cell>

        {/* index=4 → รับเงินสด */}
        <Table.Summary.Cell index={4} align="right" style={cellBase}>
          {label("รับเงินสด")}
          {val(t.cash_amount, "#27ae60")}
        </Table.Summary.Cell>

        {/* index=5 → ส่วนลด */}
        <Table.Summary.Cell index={5} align="right" style={cellBase}>
          {label("ส่วนลด")}
          {val(t.discount, "#e67e22")}
        </Table.Summary.Cell>

        {/* index=6 → เบี้ยปรับ */}
        <Table.Summary.Cell index={6} align="right" style={cellBase}>
          {label("เบี้ยปรับ")}
          {val(t.penalty, "#8e44ad")}
        </Table.Summary.Cell>

        {/* index=7 → ยอดสุทธิ NET */}
        <Table.Summary.Cell
          index={7}
          align="right"
          style={{ ...cellBase, background: "#eafaf1" }}
        >
          {label("ยอดสุทธิ (NET)", "#27ae60")}
          <div style={{ color: "#1e8449", fontSize: 14, fontWeight: "bold" }}>
            {currencyFormatPoint(t.net_payment)}
          </div>
        </Table.Summary.Cell>

        {/* index=8 → ยอดสะสมอดีต */}
        <Table.Summary.Cell index={8} align="right" style={cellBase}>
          {label("ยอดสะสมอดีต")}
          {val(t.historical_total)}
          {label("ดอกเบี้ยสะสม")}
          {val(t.historical_profit, "#888")}
        </Table.Summary.Cell>

        {/* index=9 → ตัดทุนเดิม / ตัดดอกเบี้ย */}
        <Table.Summary.Cell index={9} align="right" style={cellBase}>
          {label("ตัดทุนเดิม")}
          {val(t.cut_principal, "#c0392b")}
          {label("ตัดดอกเบี้ย")}
          {val(t.cut_profit, "#e74c3c")}
        </Table.Summary.Cell>

        {/* index=10 → ตัดทุนคงเหลือ / กำไรคงเหลือ */}
        <Table.Summary.Cell index={10} align="right" style={cellBase}>
          {label("ทุนคงเหลือ")}
          {val(t.remaining_cost, "#2980b9")}
          {label("กำไรคงเหลือ")}
          {val(t.remaining_profit, "#27ae60")}
        </Table.Summary.Cell>

        {/* index=11 → ประเภทรับ (ไม่รวม) */}
        <Table.Summary.Cell index={11} style={cellBase} />
      </Table.Summary.Row>
    );
  };

  const hasDate = startDate && endDate;
  const filteredData = useMemo(() => handleFilterData(), [handleFilterData]);

  return (
    <>
      <style>{`
        .row-even td { background-color: #f5f8ff !important; }
        .row-odd  td { background-color: #ffffff !important; }
      `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Row>
          <Col span={18}>
            <Space style={{ marginBottom: 10 }}>
              เริ่ม
              <ThaiDatePicker
                size="large"
                value={startDate}
                onChange={setStartDate}
                placeholder={"วันที่เริ่มต้น"}
                allowClear={false}
              />
              ถึง
              <ThaiDatePicker
                size="large"
                value={endDate}
                onChange={setEndDate}
                placeholder={"วันที่สิ้นสุด"}
                allowClear={false}
              />
              <Button variant="outlined" color="blue" onClick={onQuery}>
                ค้นหาตามวันที่
              </Button>
              <Input
                size="small"
                allowClear
                placeholder="ค้นหาเลขสัญญา / เลขที่บิล"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                style={{ width: 220 }}
              />
              {/* <Input
                onChange={(e) => {
                  const v = e.target.value.trim();
                  setSearchContno(v);
                }}
              />
              <Button
                variant="outlined"
                color="orange"
                disabled={!searchContno}
                onClick={() => {
                  setIsModal(true);
                }}
              >
                ค้นหารายสัญญา
              </Button> */}
            </Space>
          </Col>
          <Col span={6} style={{ textAlign: "end" }}>
            <Space style={{ marginBottom: 10 }}>
              {" "}
              <Dropdown menu={exportMenuItems} disabled={!filteredData.length}>
                <Button size="large" icon={<PrinterOutlined />}>
                  พิมพ์ / Export <DownOutlined />
                </Button>
              </Dropdown>
            </Space>
          </Col>
        </Row>

        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>
            รายงานรับรู้กำไรตามวันที่ชำระ
          </Divider>
          <Typography.Text
            strong
            style={{ display: "block", textAlign: "center", fontSize: 16 }}
          >
            บริษัท วัน เอเอ็มซี จำกัด (สำนักงานใหญ่)
          </Typography.Text>
          {hasDate && (
            <Typography.Text
              style={{ display: "block", textAlign: "center", fontSize: 16 }}
            >
              จากวันที่ {dayjs(startDate).format("DD/MM/YYYY")} ถึงวันที่{" "}
              {dayjs(endDate).format("DD/MM/YYYY")}
            </Typography.Text>
          )}
          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey={(r) => r.receipt_no ?? r.contract_no}
            rowClassName={(_, i) => (i % 2 === 0 ? "row-even" : "row-odd")}
            scroll={{ x: 1500 }}
            pagination={{
              current: pagination.page,
              pageSize: pagination.limit,
              total: pagination.total,
              showSizeChanger: false,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
            }}
            onChange={(pag) => {
              queryData(currentPayload, pag.current, pagination.limit);
            }}
            summary={tableSummary}
          />
        </Card>
        {isModal && (
          <SearchContno
            open={isModal}
            close={() => setIsModal(false)}
            contno={searchContno}
          />
        )}
      </Spin>
    </>
  );
};

const MainReportProfitRecognition = MotionHoc(Main);
export default MainReportProfitRecognition;
